import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { MdBusiness, MdFitnessCenter, MdSchedule } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import ClassDetailsModal from 'views/admin/calendar/components/InstructorClassDetailsModal';
import {
  buildAssignedInstructorClassesQuery,
  filterInstructorVisibleClasses,
  sortAssignedClassesByDate,
} from 'utils/instructorClassQueries';
import { getClassDisplayText, getClassDurationMinutes } from 'utils/instructorClassWorkflows';
import { getClassTypeIcon } from 'utils/classIcons';

const db = getFirestore(app);

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles',
  });
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Los_Angeles',
  });
};

export default function InstructorUpcomingClasses(props) {
  const { ...rest } = props;
  const { userDocId } = useContext(AuthContext);
  const history = useHistory();
  const [classes, setClasses] = useState([]);
  const [facilitiesById, setFacilitiesById] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const facilityTagBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const iconFilter = 'brightness(0) invert(1)';

  useEffect(() => {
    if (!userDocId) {
      setClasses([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const assignedQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const nextClasses = snapshot.docs.map((classDoc) => ({
          id: classDoc.id,
          ...classDoc.data(),
        }));
        setClasses(sortAssignedClassesByDate(filterInstructorVisibleClasses(nextClasses)));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading instructor dashboard classes:', error);
        setLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userDocId]);

  const upcomingClasses = useMemo(
    () => classes.filter((classData) => Number(classData.EndDate || classData.Date || 0) >= Date.now()),
    [classes]
  );

  const visibleClasses = upcomingClasses.slice(0, 3);

  useEffect(() => {
    let isActive = true;
    const missingFacilityIds = Array.from(new Set(
      visibleClasses
        .map((classData) => classData.FacilityID)
        .filter((facilityId) => facilityId && !facilitiesById[facilityId])
    ));

    if (missingFacilityIds.length === 0) return undefined;

    const loadFacilities = async () => {
      const entries = await Promise.all(missingFacilityIds.map(async (facilityId) => {
        const facilityDoc = await getDoc(doc(db, 'Users', facilityId));
        return [facilityId, facilityDoc?.exists?.() ? facilityDoc.data() : null];
      }));

      if (!isActive) return;
      setFacilitiesById((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    };

    loadFacilities();

    return () => {
      isActive = false;
    };
  }, [visibleClasses, facilitiesById]);

  return (
    <Card p="20px" align="center" direction="column" w="100%" {...rest}>
      <Flex alignItems="center" w="100%" mb="30px" justify="space-between">
        <Flex alignItems="center">
          <Icon as={MdSchedule} color={brandColor} w="28px" h="28px" me="12px" />
          <Text color={textColor} fontSize="xl" fontWeight="700" lineHeight="100%">
            Upcoming Classes
          </Text>
        </Flex>
        {!loading && upcomingClasses.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="brand"
            onClick={() => history.push('/admin/calendar')}
          >
            View All
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" w="100%" py="60px">
          <Spinner color={brandColor} size="xl" thickness="4px" />
        </Flex>
      ) : visibleClasses.length === 0 ? (
        <Flex direction="column" align="center" justify="center" w="100%" py="60px">
          <Icon as={MdFitnessCenter} color={textColorSecondary} w="64px" h="64px" mb="16px" opacity={0.3} />
          <Text color={textColorSecondary} fontSize="md" fontWeight="500">
            No upcoming classes scheduled
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" w="100%" gap="12px">
          {visibleClasses.map((classItem) => {
            const facility = facilitiesById[classItem.FacilityID] || {};
            const facilityName = getClassDisplayText(
              facility.FacilityName || facility.FirstName || classItem.FacilityName,
              'Facility'
            );
            const className = getClassDisplayText(
              classItem.ClassName || classItem.ClassType,
              'Unnamed Class'
            );
            const classIcon = getClassTypeIcon(classItem.ClassName || classItem.ClassType);

            return (
              <Flex
                key={classItem.id}
                data-testid={`upcoming-class-card-${classItem.id}`}
                w="100%"
                p="16px"
                bg={cardBg}
                borderRadius="12px"
                border="1px solid"
                borderColor={borderColor}
                transition="all 0.2s"
                _hover={{ borderColor: brandColor, boxShadow: 'sm' }}
                align="center"
                justify="space-between"
                gap="12px"
                wrap="wrap"
                cursor="pointer"
                onClick={() => setSelectedClass(classItem)}
                position="relative"
              >
                <Flex
                  position="absolute"
                  top="12px"
                  left="16px"
                  align="center"
                  gap="4px"
                  bg={facilityTagBg}
                  px="8px"
                  py="4px"
                  borderRadius="6px"
                >
                  <Icon as={MdBusiness} color={brandColor} w="14px" h="14px" flexShrink="0" />
                  <Text color={brandColor} fontSize="xs" fontWeight="600" whiteSpace="nowrap">
                    {facilityName}
                  </Text>
                </Flex>

                <Flex align="center" flex="1" minW="200px" mt="32px">
                  <Flex
                    bg={brandColor}
                    borderRadius="10px"
                    w="40px"
                    h="40px"
                    align="center"
                    justify="center"
                    me="12px"
                    flexShrink="0"
                  >
                    {classIcon ? (
                      <Image
                        data-testid="upcoming-class-type-icon"
                        src={classIcon}
                        alt={className}
                        w="24px"
                        h="24px"
                        objectFit="contain"
                        filter={iconFilter}
                      />
                    ) : (
                      <Icon as={MdFitnessCenter} color="white" w="20px" h="20px" />
                    )}
                  </Flex>
                  <Box minW="0" flex="1">
                    <Text color={textColor} fontSize="md" fontWeight="700" lineHeight="1.2" noOfLines={1}>
                      {className}
                    </Text>
                    <Text color={textColorSecondary} fontSize="xs" fontWeight="500" mt="2px">
                      {formatDate(classItem.Date)} • {formatTime(classItem.Date)}
                    </Text>
                  </Box>
                </Flex>

                <Flex
                  data-testid="upcoming-class-facility-block"
                  direction="column"
                  align="center"
                  flexShrink="0"
                  minW="160px"
                  maxW={{ base: '100%', md: '220px' }}
                  gap="8px"
                >
                  <Flex
                    data-testid="upcoming-class-facility-icon"
                    w="32px"
                    h="32px"
                    borderRadius="10px"
                    bg={facilityTagBg}
                    align="center"
                    justify="center"
                    flexShrink="0"
                  >
                    <Icon as={MdBusiness} color={brandColor} w="18px" h="18px" />
                  </Flex>
                  <Box minW="0" w="100%" textAlign="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700" lineHeight="1.2">
                      {facilityName}
                    </Text>
                    <Text
                      data-testid="upcoming-class-facility-address"
                      color={textColorSecondary}
                      fontSize="xs"
                      lineHeight="1.35"
                      mt="3px"
                      whiteSpace="normal"
                      overflowWrap="anywhere"
                    >
                      {getClassDisplayText(facility.Address || classItem.Address, 'Address unavailable')}
                    </Text>
                  </Box>
                </Flex>

                <Flex align="center" gap="10px" flexShrink="0">
                  <Badge colorScheme="brand" fontSize="xs" fontWeight="600" px="8px" py="4px" borderRadius="6px">
                    {getClassDurationMinutes(classItem)} minutes
                  </Badge>
                  <Button size="xs" variant="ghost" colorScheme="brand" pointerEvents="none">
                    Details
                  </Button>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}

      <ClassDetailsModal
        isOpen={Boolean(selectedClass)}
        onClose={() => setSelectedClass(null)}
        classData={selectedClass}
        facility={selectedClass ? facilitiesById[selectedClass.FacilityID] : null}
      />
    </Card>
  );
}
